import { IntegrationConfigurator } from "@/components/molecules/integration-configurator";
import { IntegrationLogo } from "@/components/molecules/integration-logo";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useCurrentClient } from "@/features/clients/hooks/use-clients";
import { ExternalAccountsAPI } from "@/features/external-accounts/api/external-accounts";
import {
  getAvailableCapabilities,
  PROVIDERS,
} from "@/features/external-accounts/constants";
import {
  useExternalAccounts,
  useSetupCalDAV,
  useSetupICS,
} from "@/features/external-accounts/hooks/use-external-accounts";
import { useIntegrationName } from "@/features/integrations/hooks/use-integration-name";
import { useOAuthPopup } from "@/features/external-accounts/hooks/use-oauth-popup";
import { useOAuthErrorMessage } from "@/features/external-accounts/oauth-errors";
import type {
  ExternalAccountCapability,
  ExternalAccountProvider,
} from "@/features/external-accounts/types";
import type { CheckedIntegration } from "@/features/integrations/types";
import { useTranslation } from "@/features/utils/i18n";
import { ArrowRightIcon, Plus } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

type ConnectionStep = "select" | "configure";

interface ConnectAccountDialogProps {
  onAccountCreated?: () => void;
}

// Helper component for error display
const ErrorDisplay = ({
  error,
  t,
}: {
  error: string | null;
  t: (key: string) => string;
}) => {
  if (!error) return null;

  return (
    <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm dark:border-red-900 dark:bg-red-950">
      <div className="font-medium text-red-900 dark:text-red-100 mb-1">
        ❌ {t("externalAccounts.connectDialog.connectionFailed")}
      </div>
      <div className="text-red-800 dark:text-red-200 text-xs">{error}</div>
    </div>
  );
};

export const ConnectAccountDialog = ({
  onAccountCreated,
}: ConnectAccountDialogProps) => {
  const { t } = useTranslation("externalAccounts");
  const integrationName = useIntegrationName();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<ConnectionStep>("select");
  const [provider, setProvider] = useState<ExternalAccountProvider | null>(
    null,
  );
  const [capabilities, setCapabilities] = useState<ExternalAccountCapability[]>(
    [],
  );

  // No longer need individual account settings state

  // CalDAV fields
  const [davServerUrl, setCaldavServerUrl] = useState("");
  const [davUsername, setCaldavUsername] = useState("");
  const [davPassword, setCaldavPassword] = useState("");
  const [davName, setCaldavName] = useState("");

  // ICS fields
  const [icsUrl, setIcsUrl] = useState("");
  const [icsName, setIcsName] = useState("");

  const [error, setError] = useState<string | null>(null);

  // A booking software is connected in its own configurator, checked with the
  // provider before the account exists.
  const [booking, setBooking] = useState<CheckedIntegration | null>(null);

  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";
  const setupCalDAV = useSetupCalDAV();
  const setupICS = useSetupICS();
  const openOAuthPopup = useOAuthPopup();
  const oauthErrorMessage = useOAuthErrorMessage();
  useExternalAccounts(); // Keep query active for refetch

  const handleSelectProvider = (selectedProvider: ExternalAccountProvider) => {
    setProvider(selectedProvider);
    setStep("configure");
    setError(null); // Clear any previous errors
  };

  const handleBack = () => {
    setStep("select");
    setProvider(null);
    setError(null); // Clear error when going back
  };

  // Common handler for after account creation
  const handleAccountCreated = (_accountId: string, successMessage: string) => {
    toast.success(successMessage);
    handleClose();

    // Notify parent to open global sync settings
    setTimeout(() => {
      onAccountCreated?.();
    }, 500);
  };

  // Common error handler
  const handleConnectionError = (error: unknown, defaultMessage: string) => {
    const errorMessage =
      error instanceof Error ? error.message : defaultMessage;
    setError(errorMessage);
    toast.error(errorMessage);
  };

  useEffect(() => {
    if (!open) {
      // Reset form state after dialog closes
      setTimeout(() => {
        setStep("select");
        setProvider(null);
        setCapabilities([]);
        setCaldavServerUrl("");
        setCaldavUsername("");
        setCaldavPassword("");
        setCaldavName("");
        setIcsUrl("");
        setIcsName("");
        setError(null); // Clear error on close
      }, 200);
    }
  }, [open]);

  const handleClose = () => {
    setOpen(false);
  };

  const handleOAuthConnect = async () => {
    try {
      // The URL is fetched while the popup is already open: a `window.open()`
      // that runs after the await has lost its user gesture and gets blocked
      // (see `useOAuthPopup`).
      const authUrl = ExternalAccountsAPI.getAuthorizationUrl(
        clientId,
        provider as "google" | "microsoft",
        capabilities,
      );

      // The account is created inside the popup, so we have to wait for it and
      // refetch — otherwise a successful connection stays invisible until the
      // page is reloaded by hand.
      const opened = openOAuthPopup(authUrl);

      setOpen(false);
      toast.info(t("externalAccounts.messages.oauthInfo"));

      const result = await opened;

      // `success` is the hook's verdict *after* it has re-read the accounts, so
      // it means an account exists — not merely that the window went away. This
      // screen used to celebrate the latter, which is how a consent that
      // connected nothing was announced as a connection.
      if (result.success) {
        toast.success(t("externalAccounts.messages.oauthConnected"));
        onAccountCreated?.();
      } else {
        toast.error(oauthErrorMessage(result));
      }
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t("externalAccounts.messages.oauthError"),
      );
    }
  };

  /**
   * Google Meet: a Google account connected for its calendar events only
   * (capability `video`) — it hosts meetings and nothing of it is synced.
   */
  const handleGoogleMeet = async () => {
    try {
      const opened = openOAuthPopup(
        ExternalAccountsAPI.getAuthorizationUrl(clientId, "google", ["video"]),
      );
      setOpen(false);
      toast.info(t("externalAccounts.messages.oauthInfo"));
      const result = await opened;
      if (result.success) {
        toast.success(t("externalAccounts.messages.oauthConnected"));
        onAccountCreated?.();
      } else {
        toast.error(oauthErrorMessage(result));
      }
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t("externalAccounts.messages.oauthError"),
      );
    }
  };

  const handleCalDAVSetup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      const result = await setupCalDAV.mutateAsync({
        server_url: davServerUrl,
        username: davUsername,
        password: davPassword,
        name: davName || `calDAV - ${davUsername}`,
      });

      handleAccountCreated(
        result.account.id,
        t("externalAccounts.messages.caldavConnected"),
      );
    } catch (error) {
      handleConnectionError(error, t("externalAccounts.messages.caldavError"));
    }
  };

  const handleICSSetup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      const result = await setupICS.mutateAsync({
        url: icsUrl,
        name: icsName || t("connectDialog.icsDefaultName"),
      });

      handleAccountCreated(
        result.account.id,
        t("externalAccounts.messages.icsConnected"),
      );
    } catch (error) {
      handleConnectionError(error, t("externalAccounts.messages.icsError"));
    }
  };

  const toggleCapability = (capability: ExternalAccountCapability) => {
    setCapabilities((prev) =>
      prev.includes(capability)
        ? prev.filter((c) => c !== capability)
        : [...prev, capability],
    );
  };

  const capabilitiesList = getAvailableCapabilities(provider);

  return (
    <>
      {booking && (
        <IntegrationConfigurator
          integration={booking}
          open={!!booking}
          onOpenChange={(next) => !next && setBooking(null)}
          initialValues={{}}
          onSaved={() =>
            toast.success(t("externalAccounts.messages.bookingConnected"))
          }
        />
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button>
            <Plus className="h-4 w-4 mr-2" />
            {t("externalAccounts.buttons.connectAccount")}
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {t("externalAccounts.connectDialog.title")}
            </DialogTitle>
            <DialogDescription>
              {step === "select"
                ? t("externalAccounts.connectDialog.descriptionSelect")
                : t("externalAccounts.connectDialog.descriptionConfigure")}
            </DialogDescription>
          </DialogHeader>

          {step === "select" && (
            <div className="space-y-5">
              <ProviderGroup
                title={t("externalAccounts.connectDialog.groups.calendars")}
              >
                {PROVIDERS.map((id) => (
                  <ProviderButton
                    key={id}
                    logo={id}
                    name={t(`providers.${id}.name`, integrationName(id))}
                    description={t(`providers.${id}.description`, "")}
                    onClick={() => handleSelectProvider(id)}
                  />
                ))}
              </ProviderGroup>
              <ProviderGroup
                title={t("externalAccounts.connectDialog.groups.video")}
              >
                <ProviderButton
                  logo="google_meet"
                  name={t("providers.google_meet.name")}
                  description={t("providers.google_meet.description")}
                  onClick={handleGoogleMeet}
                />
              </ProviderGroup>
              <ProviderGroup
                title={t("externalAccounts.connectDialog.groups.booking")}
              >
                {BOOKING_SOFTWARE.map(({ integration, logo }) => (
                  <ProviderButton
                    key={integration}
                    logo={logo}
                    name={t(`providers.${logo}.name`)}
                    description={t(`providers.${logo}.description`)}
                    onClick={() => {
                      setOpen(false);
                      setBooking(integration);
                    }}
                  />
                ))}
              </ProviderGroup>
            </div>
          )}

          {step === "configure" &&
            provider !== "carddav" &&
            provider !== "caldav" &&
            provider !== "ics" && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>
                    {t("externalAccounts.connectDialog.capabilities")}
                  </Label>
                  <div className="space-y-2">
                    {capabilitiesList.map((cap) => (
                      <Button
                        id={cap.id}
                        variant={"outline"}
                        size="inline"
                        className="w-full"
                        onClick={() => toggleCapability(cap.id)}
                      >
                        <div className="w-full flex items-center gap-4 p-4 text-left">
                          <Checkbox checked={capabilities.includes(cap.id)} />
                          <label htmlFor={cap.id} className="grow">
                            {t(`capabilities.${cap.id}.name`, cap.name)}
                            <br />
                            <span className="text-sm text-muted-foreground font-normal">
                              {t(
                                `capabilities.${cap.id}.description`,
                                cap.description,
                              )}
                            </span>
                          </label>
                          <cap.icon className="h-8! w-8!" weight="fill" />
                        </div>
                      </Button>
                    ))}
                  </div>
                </div>

                <DialogFooter className="gap-2">
                  <Button variant="outline" onClick={handleBack}>
                    {t("externalAccounts.buttons.back")}
                  </Button>
                  <Button
                    onClick={handleOAuthConnect}
                    className="flex-1"
                    disabled={capabilities.length === 0}
                  >
                    {t("externalAccounts.buttons.connect")}
                  </Button>
                </DialogFooter>
              </div>
            )}

          {step === "configure" &&
            (provider === "carddav" || provider === "caldav") && (
              <form onSubmit={handleCalDAVSetup} className="space-y-4">
                <ErrorDisplay error={error} t={t} />

                <div className="space-y-2">
                  <Label htmlFor="server_url">
                    {t("externalAccounts.connectDialog.caldavServerUrl")} *
                  </Label>
                  <Input
                    id="server_url"
                    placeholder={t(
                      "externalAccounts.connectDialog.caldavServerUrlPlaceholder",
                    )}
                    value={davServerUrl}
                    onChange={(e) => setCaldavServerUrl(e.target.value)}
                    required
                  />
                  <p className="text-xs text-muted-foreground">
                    {t("externalAccounts.connectDialog.caldavServerUrlHint")}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="username">
                    {t("externalAccounts.connectDialog.username")} *
                  </Label>
                  <Input
                    id="username"
                    placeholder={t(
                      "externalAccounts.connectDialog.usernamePlaceholder",
                    )}
                    value={davUsername}
                    onChange={(e) => setCaldavUsername(e.target.value)}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">
                    {t("externalAccounts.connectDialog.password")} *
                  </Label>
                  <Input
                    id="password"
                    type="password"
                    value={davPassword}
                    onChange={(e) => setCaldavPassword(e.target.value)}
                    required
                  />
                  <p className="text-xs text-muted-foreground">
                    {t("externalAccounts.connectDialog.passwordHint")}
                  </p>
                </div>

                <DialogFooter className="gap-2">
                  <Button type="button" variant="outline" onClick={handleBack}>
                    {t("externalAccounts.buttons.back")}
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1"
                    disabled={setupCalDAV.isPending}
                  >
                    {t("externalAccounts.buttons.connect")}
                  </Button>
                </DialogFooter>
              </form>
            )}

          {step === "configure" && provider === "ics" && (
            <form onSubmit={handleICSSetup} className="space-y-4">
              <ErrorDisplay error={error} t={t} />

              <div className="space-y-2">
                <Label htmlFor="ics_url">
                  {t("externalAccounts.connectDialog.icsUrl")} *
                </Label>
                <Input
                  id="ics_url"
                  placeholder={t(
                    "externalAccounts.connectDialog.icsUrlPlaceholder",
                  )}
                  value={icsUrl}
                  onChange={(e) => setIcsUrl(e.target.value)}
                  required
                  type="url"
                />
                <p className="text-xs text-muted-foreground">
                  {t("externalAccounts.connectDialog.icsUrlHint")}
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="ics_name">
                  {t("externalAccounts.connectDialog.icsName")}
                </Label>
                <Input
                  id="ics_name"
                  placeholder={t(
                    "externalAccounts.connectDialog.icsNamePlaceholder",
                  )}
                  value={icsName}
                  onChange={(e) => setIcsName(e.target.value)}
                />
              </div>

              <DialogFooter className="gap-2">
                <Button type="button" variant="outline" onClick={handleBack}>
                  {t("externalAccounts.buttons.back")}
                </Button>
                <Button
                  type="submit"
                  className="flex-1"
                  disabled={setupICS.isPending}
                >
                  {t("externalAccounts.buttons.connect")}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

/** The booking software a tenant can connect, in the order they are offered. */
const BOOKING_SOFTWARE: { integration: CheckedIntegration; logo: string }[] = [
  { integration: "provalliance_backoffice", logo: "provalliance" },
  { integration: "calendly", logo: "calendly" },
  { integration: "virtual_browser", logo: "website" },
];

const ProviderGroup = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <div className="space-y-2">
    <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
      {title}
    </div>
    <div className="space-y-2">{children}</div>
  </div>
);

const ProviderButton = ({
  logo,
  name,
  description,
  onClick,
}: {
  logo: string;
  name: string;
  description: string;
  onClick: () => void;
}) => (
  <Button variant="outline" size="inline" className="w-full" onClick={onClick}>
    <div className="w-full flex items-center gap-4 p-3 text-left">
      <IntegrationLogo id={logo} />
      <div className="grow">
        <div className="font-medium">{name}</div>
        <div className="text-sm text-muted-foreground font-normal whitespace-normal">
          {description}
        </div>
      </div>
      <ArrowRightIcon className="h-5 w-5 text-muted-foreground shrink-0 block" />
    </div>
  </Button>
);
